import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Paper, Typography, Tabs, Tab, Select, MenuItem, FormControl, InputLabel, ToggleButtonGroup, ToggleButton } from '@mui/material'
import { Plus, Edit3, XCircle, Globe, FileText, Scale, Layers } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { setCountry, setDocType, fetchReferenceData } from '../../redux/actions/dataUpdateAction'
import AddDocumentTab from './components/AddDocumentTab'
import EditDocumentTab from './components/EditDocumentTab'
import AbrogateDocumentTab from './components/AbrogateDocumentTab'
import BOConsolidationTab from './components/BOConsolidationTab'

const DATA_TABS = [
  { key: 'add', label_key: 'du_add_document', icon: Plus },
  { key: 'edit', label_key: 'du_edit_document', icon: Edit3 },
  { key: 'abrogate', label_key: 'du_abrogate', icon: XCircle },
  { key: 'bo-consolidation', label_key: 'du_bo_consolidation', icon: Layers },
]

export default function DataUpdateScreen() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { countryCode, docType } = useSelector((s) => s.dataUpdate)
  const navigate = useNavigate()
  const location = useLocation()

  // Derive active sub-tab from URL
  const segments = location.pathname.replace(/^\/admin\/data-update\/?/, '').split('/')
  const activeKey = DATA_TABS.find((tab) => tab.key === segments[0])?.key || 'add'
  const activeTabIndex = DATA_TABS.findIndex((tab) => tab.key === activeKey)

  const handleTabChange = (_, newIndex) => {
    const tab = DATA_TABS[newIndex]
    if (tab) {
      navigate(`/admin/data-update/${tab.key}`)
    }
  }

  const COUNTRIES = [
    { code: 'MA', label: t('du_morocco') },
    { code: 'AE', label: t('du_uae') },
  ]

  useEffect(() => {
    dispatch(fetchReferenceData(countryCode))
  }, [countryCode, dispatch])

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      {/* Header */}
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, fontFamily: '"Cormorant Garamond", serif' }}>
        {t('du_title')}
      </Typography>

      {/* Controls Bar */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 3, borderRadius: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Globe size={14} /> {t('du_country')}</InputLabel>
          <Select
            value={countryCode}
            label={t('du_country')}
            onChange={(e) => dispatch(setCountry(e.target.value))}
          >
            {COUNTRIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={docType}
          exclusive
          onChange={(_, val) => val && dispatch(setDocType(val))}
          size="small"
        >
          <ToggleButton value="legislation" sx={{ textTransform: 'none', px: 2 }}>
            <FileText size={16} style={{ marginRight: 6 }} /> {t('du_legislation')}
          </ToggleButton>
          <ToggleButton value="case_law" sx={{ textTransform: 'none', px: 2 }}>
            <Scale size={16} style={{ marginRight: 6 }} /> {t('du_case_law')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Tabs
          value={activeTabIndex}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minHeight: 48 },
            '& .Mui-selected': { color: '#c9a96e' },
            '& .MuiTabs-indicator': { backgroundColor: '#c9a96e' },
          }}
        >
          {DATA_TABS.map((tab) => {
            const Icon = tab.icon
            return (
              <Tab key={tab.key} icon={<Icon size={16} />} iconPosition="start" label={t(tab.label_key)} />
            )
          })}
        </Tabs>

        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          {activeKey === 'add' && <AddDocumentTab />}
          {activeKey === 'edit' && <EditDocumentTab />}
          {activeKey === 'abrogate' && <AbrogateDocumentTab />}
          {activeKey === 'bo-consolidation' && <BOConsolidationTab />}
        </Box>
      </Paper>
    </Box>
  )
}
